import { useCallback, useEffect, useState } from "react";
import {
  App as AntdApp,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { apiClient } from "../../../services/api/apiClient";
import { PageHeader } from "../../../components/common/PageHeader";
import { SproutIcon } from "../../../components/common/SproutIcon";
import { usePageTitle } from "../../../hooks";

const { Text } = Typography;

interface Variety {
  id: number;
  name: string;
  code: string;
  is_active: boolean;
  description: string | null;
  name_en: string;
  name_it: string;
  name_pt: string;
  english_name: string;
  usage_count: number;
}

interface VarietyFormValues {
  name: string;
  code: string;
  is_active?: boolean;
  description?: string | null;
  name_en?: string;
  name_it?: string;
  name_pt?: string;
}

const slugify = (value: string) =>
  value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);

/**
 * Admin page for `Variety` — catálogo del cultivo principal (Tango,
 * Kinnow LS, …). Es el modelo nuevo introducido en la PR1 que
 * sustituye al tuple `VARIETY` hardcodeado del backend.
 *
 * Soporta CRUD con soft-delete: si la variedad está referenciada por
 * Seasons/parcelas, el backend bloquea el DELETE y la UI ofrece
 * "Desactivar" como alternativa.
 *
 * El campo `code` es el identificador estable entre apps
 * (intranet ↔ Certification). Se auto-sugiere desde `name` pero el
 * admin puede sobreescribirlo. Una vez creado, NO se debe cambiar
 * (se referencia por valor desde otras apps).
 */
export const VarietiesList = () => {
  const { t } = useTranslation();
  const { message: notify } = AntdApp.useApp();
  const [data, setData] = useState<Variety[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(true);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<Variety | null>(null);
  const [form] = Form.useForm<VarietyFormValues>();
  const [saving, setSaving] = useState(false);
  // Quien edita un código ya creado se autoinflige problemas con
  // otras apps que lo referencian: bloqueamos la edición salvo que
  // el usuario lo desbloquee explícitamente.
  const [codeUnlocked, setCodeUnlocked] = useState(false);

  usePageTitle(t("varieties.title", "Variedades Protegidas"));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get<Variety[]>("/admin/varieties/");
      const list = Array.isArray(r.data)
        ? r.data
        : ((r.data as any).results ?? []);
      setData(list);
    } catch {
      notify.error(
        t(
          "varieties.load_error",
          "Error al cargar las variedades protegidas",
        ),
      );
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [notify, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // ----- CRUD ---------------------------------------------------------

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true });
    setCodeUnlocked(true);
    setEditorOpen(true);
  };

  const openEdit = (row: Variety) => {
    setEditing(row);
    form.setFieldsValue({
      name: row.name,
      code: row.code,
      is_active: row.is_active,
      description: row.description ?? "",
      name_en: row.name_en ?? "",
      name_it: row.name_it ?? "",
      name_pt: row.name_pt ?? "",
    });
    setCodeUnlocked(false);
    setEditorOpen(true);
  };

  // Auto-suggest `code` desde `name` mientras el usuario escribe en
  // un alta nueva. Si el admin ya tocó manualmente el code, no lo
  // pisamos.
  const onNameChange = (value: string) => {
    if (editing) return;
    const current = form.getFieldValue("code");
    const suggested = slugify(value);
    if (!current || current === slugify(form.getFieldValue("name") || "")) {
      form.setFieldsValue({ code: suggested });
    }
  };

  const submitEditor = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      if (editing) {
        await apiClient.patch(`/admin/varieties/${editing.id}/`, values);
        notify.success(
          t("varieties.update_success", "Variedad protegida actualizada"),
        );
      } else {
        await apiClient.post("/admin/varieties/", values);
        notify.success(
          t("varieties.create_success", "Variedad protegida creada"),
        );
      }
      setEditorOpen(false);
      await load();
    } catch (e: any) {
      const errBody = e?.response?.data;
      const msg =
        errBody?.detail ??
        errBody?.code?.[0] ??
        errBody?.name?.[0] ??
        t(
          "varieties.save_error",
          "No se pudo guardar la variedad protegida",
        );
      notify.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const deleteVariety = async (row: Variety) => {
    try {
      await apiClient.delete(`/admin/varieties/${row.id}/`);
      notify.success(
        t("varieties.delete_success", "Variedad protegida eliminada"),
      );
      await load();
    } catch (e: any) {
      const msg =
        e?.response?.data?.detail ??
        t(
          "varieties.delete_error",
          "No se pudo eliminar la variedad protegida",
        );
      notify.error(msg);
    }
  };

  const toggleActive = async (row: Variety, next: boolean) => {
    try {
      await apiClient.patch(`/admin/varieties/${row.id}/`, { is_active: next });
      await load();
    } catch (e: any) {
      const msg =
        e?.response?.data?.detail ??
        t(
          "varieties.save_error",
          "No se pudo guardar la variedad protegida",
        );
      notify.error(msg);
    }
  };

  // ----- table --------------------------------------------------------

  const rows = showInactive ? data : data.filter((v) => v.is_active);

  const columns = [
    {
      title: t("varieties.name", "Nombre"),
      dataIndex: "name",
      key: "name",
      sorter: (a: Variety, b: Variety) => a.name.localeCompare(b.name),
      render: (v: string, row: Variety) => (
        <span style={{ opacity: row.is_active ? 1 : 0.55 }}>
          <strong>{v}</strong>
          {!row.is_active && (
            <Tag color="default" style={{ marginLeft: 8 }}>
              {t("varieties.inactive", "Archivada")}
            </Tag>
          )}
        </span>
      ),
    },
    {
      title: t("varieties.code", "Código"),
      dataIndex: "code",
      key: "code",
      width: 180,
      render: (v: string) => (
        <Tooltip
          title={t(
            "varieties.code_tooltip",
            "Identificador estable entre apps. No lo cambies una vez en uso.",
          )}
        >
          <code style={{ fontSize: 13 }}>{v}</code>
        </Tooltip>
      ),
      sorter: (a: Variety, b: Variety) => a.code.localeCompare(b.code),
    },
    {
      title: t("varieties.translations", "Traducciones"),
      key: "translations",
      width: 200,
      render: (_: any, row: Variety) => {
        const entries: Array<{ code: string; label: string; value: string }> = [
          { code: "EN", label: "English", value: row.name_en },
          { code: "IT", label: "Italiano", value: row.name_it },
          { code: "PT", label: "Português", value: row.name_pt },
        ];
        return (
          <Space size={4} wrap>
            {entries.map((e) =>
              e.value ? (
                <Tooltip key={e.code} title={`${e.label}: ${e.value}`}>
                  <Tag color="blue" style={{ margin: 0 }}>
                    {e.code}
                  </Tag>
                </Tooltip>
              ) : (
                <Tooltip
                  key={e.code}
                  title={`${e.label}: ${t("varieties.no_translation", "sin traducir")}`}
                >
                  <Tag color="default" style={{ margin: 0, opacity: 0.55 }}>
                    {e.code}
                  </Tag>
                </Tooltip>
              ),
            )}
          </Space>
        );
      },
    },
    {
      title: t("varieties.usage_count", "Usos"),
      dataIndex: "usage_count",
      key: "usage_count",
      width: 100,
      align: "center" as const,
      sorter: (a: Variety, b: Variety) => a.usage_count - b.usage_count,
      render: (n: number) =>
        n > 0 ? <Tag color="blue">{n}</Tag> : <Tag color="default">0</Tag>,
    },
    {
      title: t("varieties.is_active", "Activa"),
      dataIndex: "is_active",
      key: "is_active",
      width: 100,
      align: "center" as const,
      render: (v: boolean, row: Variety) => (
        <Switch checked={v} onChange={(n) => toggleActive(row, n)} size="small" />
      ),
      filters: [
        { text: t("varieties.active", "Activa"), value: true },
        { text: t("varieties.inactive", "Archivada"), value: false },
      ],
      onFilter: (value: any, row: Variety) => row.is_active === value,
    },
    {
      title: t("common.actions", "Acciones"),
      key: "actions",
      width: 130,
      align: "right" as const,
      render: (_: any, row: Variety) => (
        <Space size="small">
          <Tooltip title={t("common.edit", "Editar")}>
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEdit(row)}
            />
          </Tooltip>
          <Popconfirm
            title={t(
              "varieties.delete_confirm",
              "¿Eliminar esta variedad protegida?",
            )}
            description={
              row.usage_count > 0
                ? t(
                    "varieties.delete_blocked",
                    "Está en uso: no se puede eliminar. Desmarca «Activa» para archivarla.",
                  )
                : t(
                    "varieties.delete_warning",
                    "Esta acción no se puede deshacer.",
                  )
            }
            onConfirm={() => deleteVariety(row)}
            okText={t("common.yes", "Sí")}
            cancelText={t("common.no", "No")}
            okButtonProps={{ danger: true }}
            disabled={row.usage_count > 0}
          >
            <Tooltip
              title={
                row.usage_count > 0
                  ? t(
                      "varieties.delete_blocked_tooltip",
                      "En uso — no se puede eliminar",
                    )
                  : t("common.delete", "Eliminar")
              }
            >
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                disabled={row.usage_count > 0}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <PageHeader
        icon={<SproutIcon />}
        accent="#52C41A"
        title={t("varieties.title", "Variedades Protegidas")}
        subtitle={t(
          "varieties.subtitle",
          "Catálogo de variedades protegidas del cultivo principal (Tango, Kinnow LS, …). Distintas de las variedades de las especies adyacentes. El campo «código» es el identificador estable usado por otras apps (Certification).",
        )}
        extra={
          <Space wrap>
            <Space size={6}>
              <Switch
                checked={showInactive}
                onChange={setShowInactive}
                size="small"
              />
              <span>{t("varieties.show_inactive", "Mostrar archivadas")}</span>
            </Space>
            <Button icon={<ReloadOutlined />} onClick={() => void load()}>
              {t("common.reload", "Refrescar")}
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              {t("varieties.create", "Nueva variedad protegida")}
            </Button>
          </Space>
        }
      />

      <Table<Variety>
        dataSource={rows}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 25, showSizeChanger: true }}
        scroll={{ x: 1000 }}
      />

      <Modal
        title={
          editing
            ? t("varieties.edit_title", "Editar variedad protegida")
            : t("varieties.create_title", "Nueva variedad protegida")
        }
        open={editorOpen}
        onCancel={() => (saving ? undefined : setEditorOpen(false))}
        onOk={submitEditor}
        confirmLoading={saving}
        okText={t("common.save", "Guardar")}
        cancelText={t("common.cancel", "Cancelar")}
        destroyOnHidden
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label={t("varieties.name_es", "Nombre (Español)")}
            name="name"
            rules={[
              {
                required: true,
                message: t(
                  "varieties.name_required",
                  "El nombre es obligatorio",
                ),
              },
              { max: 100 },
            ]}
            extra={t(
              "varieties.name_es_hint",
              "Idioma principal del catálogo — siempre obligatorio.",
            )}
          >
            <Input
              maxLength={100}
              placeholder={t(
                "varieties.name_placeholder",
                "Ej: TANGO / TANG GOLD",
              )}
              onChange={(e) => onNameChange(e.target.value)}
              autoFocus
            />
          </Form.Item>

          <Form.Item
            label={t("varieties.code", "Código")}
            name="code"
            rules={[
              {
                required: true,
                message: t(
                  "varieties.code_required",
                  "El código es obligatorio",
                ),
              },
              {
                pattern: /^[a-z0-9-]+$/,
                message: t(
                  "varieties.code_format",
                  "Solo minúsculas, números y guiones (ej. tango, kinnow-ls).",
                ),
              },
              { max: 64 },
            ]}
            extra={
              editing && !codeUnlocked
                ? t(
                    "varieties.code_locked_hint",
                    "Bloqueado: cambiarlo rompe la integración con otras apps. Desbloquéalo solo si sabes lo que haces.",
                  )
                : t(
                    "varieties.code_hint",
                    "Slug estable. Lo usa Certification para referenciar la variedad protegida.",
                  )
            }
          >
            <Input
              maxLength={64}
              placeholder={t("varieties.code_placeholder", "tango")}
              disabled={editing != null && !codeUnlocked}
              addonAfter={
                editing != null ? (
                  <Button
                    type="link"
                    size="small"
                    onClick={() => setCodeUnlocked((v) => !v)}
                    style={{ padding: 0, height: "auto" }}
                  >
                    {codeUnlocked
                      ? t("varieties.code_lock", "Bloquear")
                      : t("varieties.code_unlock", "Desbloquear")}
                  </Button>
                ) : null
              }
            />
          </Form.Item>

          <Form.Item
            label={t("varieties.description", "Descripción")}
            name="description"
            rules={[{ max: 2000 }]}
          >
            <Input.TextArea
              rows={2}
              maxLength={2000}
              placeholder={t(
                "varieties.description_placeholder",
                "Notas internas, características distintivas, etc.",
              )}
            />
          </Form.Item>

          <Text
            type="secondary"
            style={{
              fontSize: 12,
              display: "block",
              marginTop: 4,
              marginBottom: 8,
            }}
          >
            {t(
              "varieties.translations_hint",
              "Traducciones opcionales — se usan en los exports de Excel y en la UI según el idioma del usuario.",
            )}
          </Text>

          <Form.Item
            label={t("varieties.name_en", "Nombre (English)")}
            name="name_en"
            rules={[{ max: 100 }]}
          >
            <Input maxLength={100} placeholder="TANGO / TANG GOLD" />
          </Form.Item>

          <Form.Item
            label={t("varieties.name_it", "Nome (Italiano)")}
            name="name_it"
            rules={[{ max: 100 }]}
          >
            <Input maxLength={100} placeholder="TANGO / TANG GOLD" />
          </Form.Item>

          <Form.Item
            label={t("varieties.name_pt", "Nome (Português)")}
            name="name_pt"
            rules={[{ max: 100 }]}
          >
            <Input maxLength={100} placeholder="TANGO / TANG GOLD" />
          </Form.Item>

          {editing && (
            <Form.Item
              label={t("varieties.is_active", "Activa")}
              name="is_active"
              valuePropName="checked"
              extra={t(
                "varieties.is_active_hint",
                "Si está desmarcada, la variedad protegida desaparece de los selectores nuevos pero las parcelas que ya la usan no se tocan.",
              )}
            >
              <Switch />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </Card>
  );
};

export default VarietiesList;
